import React from 'react';
import { useTranslation } from 'localization';
import './ProvidedEntitiesSummaryMolecule.scss';

/**
 * Canonical shape consumed by `ProvidedEntitiesSummaryMolecule`. Callers
 * map their domain model (ResolvedType, ResolvedContext, ...) into this
 * shape — keeping the molecule decoupled from `useResolvedTypes` /
 * `useResolvedContexts` hook return types.
 */
export interface ProvidedEntity {
  id: string;
  /** PrimeIcon class without the `pi ` prefix (e.g. `pi-circle`). */
  icon?: string;
  displayName: string;
  description?: string;
  /** When true, renders a "forked" badge next to the name. Optional per kind. */
  isForked?: boolean;
}

export type ProvidedEntitiesKind = 'types' | 'contexts';

export interface ProvidedEntitiesSummaryMoleculeProps {
  kind: ProvidedEntitiesKind;
  items: ProvidedEntity[];
  loading?: boolean;
  className?: string;
}

const TRANSLATION_KEYS: Record<ProvidedEntitiesKind, { title: string; count: string }> = {
  types: {
    title: 'marketplace.approachDetail.providedTypes.title',
    count: 'marketplace.approachDetail.providedTypes.typeCount',
  },
  contexts: {
    title: 'marketplace.approachDetail.providedContexts.title',
    count: 'marketplace.approachDetail.providedContexts.contextCount',
  },
};

const FALLBACK_ICONS: Record<ProvidedEntitiesKind, string> = {
  types: 'pi-circle',
  contexts: 'pi-filter',
};

const ProvidedEntitiesSummaryMolecule: React.FC<ProvidedEntitiesSummaryMoleculeProps> = ({
  kind,
  items,
  loading = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = [
    'marketplace-provided-entities-summary-molecule',
    `marketplace-provided-entities-summary-molecule--${kind}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const titleKey = TRANSLATION_KEYS[kind].title;
  const countKey = TRANSLATION_KEYS[kind].count;
  const fallbackIcon = FALLBACK_ICONS[kind];

  if (loading) {
    return (
      <div className={classes}>
        <div className="marketplace-provided-entities-summary-molecule__header">
          {t(titleKey)}
        </div>
        <div className="marketplace-provided-entities-summary-molecule__grid">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="marketplace-provided-entities-summary-molecule__card marketplace-provided-entities-summary-molecule__card--skeleton"
            >
              <div className="marketplace-provided-entities-summary-molecule__skeleton-name" />
              <div className="marketplace-provided-entities-summary-molecule__skeleton-desc" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={classes}>
      <div className="marketplace-provided-entities-summary-molecule__header">
        <span>{t(titleKey)}</span>
        <span className="marketplace-provided-entities-summary-molecule__count">
          {t(countKey, { count: items.length })}
        </span>
      </div>

      <div className="marketplace-provided-entities-summary-molecule__grid">
        {items.map((item) => (
          <div
            key={item.id}
            className="marketplace-provided-entities-summary-molecule__card"
          >
            <div className="marketplace-provided-entities-summary-molecule__card-header">
              <i className={`pi ${item.icon || fallbackIcon}`} />
              <span className="marketplace-provided-entities-summary-molecule__name">
                {item.displayName}
              </span>
              {item.isForked && (
                <span className="marketplace-provided-entities-summary-molecule__forked-badge">
                  {t('marketplace.approachDetail.providedTypes.forked')}
                </span>
              )}
            </div>
            {item.description && (
              <div className="marketplace-provided-entities-summary-molecule__description">
                {item.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProvidedEntitiesSummaryMolecule;
